import { Directive, ElementRef, HostListener, Input, TemplateRef, ViewContainerRef } from '@angular/core';
import { Overlay, OverlayPositionBuilder, OverlayRef, PositionStrategy } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import { Subject } from 'rxjs';

@Directive({
    selector: '[phTooltip]',
    standalone: true
})
export class PhTooltipDirective {

	private _opened: boolean = false;
	private _hideTimeout: any;
	private _showTimeout: any;

	showSubject: Subject<void> = new Subject<void>();

	@Input() phTooltip: TemplateRef<any>;
	@Input() phTooltipDisabled: boolean = false;
	@Input() phTooltipPosition: 'top' | 'bottom' | 'left' | 'right' | 'center' = 'bottom';
	@Input() phTooltipOffsetY: number = 14;
	@Input() phTooltipOffsetX: number = 14;
	@Input() phTooltipHideDelay: number = 0;
	@Input() phTooltipShowDelay: number = 200;

	private _overlayRef: OverlayRef;

	constructor(
		private _overlay: Overlay,
		private _overlayPositionBuilder: OverlayPositionBuilder,
		private _elementRef: ElementRef,
		private _viewContainerRef: ViewContainerRef
	) {}

	ngOnInit(): void {
		const leftPosition = {
			originX: 'start',
			originY: 'center',
			overlayX: 'end',
			overlayY: 'center',
			offsetY: 0,
			offsetX: -this.phTooltipOffsetX
		},
			rightPosition = {
				originX: 'end',
				originY: 'center',
				overlayX: 'start',
				overlayY: 'center',
				offsetY: 0,
				offsetX: this.phTooltipOffsetX
			},
			topPosition = {
				originX: 'center',
				originY: 'top',
				overlayX: 'center',
				overlayY: 'bottom',
				offsetY: -this.phTooltipOffsetY,
				offsetX: 0
			},
			bottomPosition = {
				originX: 'center',
				originY: 'bottom',
				overlayX: 'center',
				overlayY: 'top',
				offsetY: this.phTooltipOffsetY,
				offsetX: 0
			};

		const positions = {
			'top': [
				topPosition,
				bottomPosition,
				rightPosition,
				leftPosition
			],
			'bottom': [
				bottomPosition,
				topPosition,
				rightPosition,
				leftPosition
			],
			'left': [
				leftPosition,
				rightPosition,
				topPosition,
				bottomPosition
			],
			'right': [
				rightPosition,
				leftPosition,
				topPosition,
				bottomPosition
			]
		}

		const positionStrategy: PositionStrategy = this._overlayPositionBuilder
			.flexibleConnectedTo(this._elementRef)
			.withPositions(positions[this.phTooltipPosition]);

		this._overlayRef = this._overlay.create({ positionStrategy });
		this._overlayRef.overlayElement.style.pointerEvents = 'auto';
		this._overlayRef.overlayElement.classList.add('ph-tooltip');
		this._overlayRef.overlayElement.classList.add(this.phTooltipPosition);

		this._overlayRef.overlayElement.addEventListener('mouseenter', () => this.show());
		this._overlayRef.overlayElement.addEventListener('mouseleave', () => this.hide());

		this.showSubject
			.subscribe(() => {
				if (this._opened && this._overlayRef && !this._overlayRef.hasAttached()) {
					this._overlayRef.attach(new TemplatePortal(this.phTooltip, this._viewContainerRef));
				}
			});
	}

	private _closeToolTip(): void {
		this._opened = false;
		if (this._overlayRef) {
			this._overlayRef.detach();
		}
	}

	ngOnDestroy(): void {
		if (this._hideTimeout) clearTimeout(this._hideTimeout);
		if (this._showTimeout) clearTimeout(this._showTimeout);
		this._closeToolTip();
		this._overlayRef.dispose();
		this.showSubject.unsubscribe();
	}

	@HostListener('mouseenter') show() {
		if (this.phTooltipDisabled) return;
		if (this._hideTimeout) {
			clearTimeout(this._hideTimeout);
			this._hideTimeout = null;
		}
		if (this._showTimeout) clearTimeout(this._showTimeout);

		this._showTimeout = setTimeout(() => {
			this._opened = true;
			this.showSubject.next();
			this._showTimeout = null;
		}, this.phTooltipShowDelay);
	}

	@HostListener('mouseleave') hide() {
		if (this._showTimeout) {
			clearTimeout(this._showTimeout);
			this._showTimeout = null;
		}
		if (this._hideTimeout) clearTimeout(this._hideTimeout);
		this._hideTimeout = setTimeout(() => {
			this._closeToolTip();
			this._hideTimeout = null;
		}, this.phTooltipHideDelay);
	}
}